import { ISyncInventoryUnitsPayload, mutateCollectionItem } from "actions/collections";
import { snackbarAdd } from "actions/snackbars";
import { COLLECTIONS } from "constants/action-types";
import { Item, ITEMS_ID } from "constants/product-collection";
import { MAX_ITEMS_PER_REQUEST } from "helpers/product-collection/inventory-units";
import { flatToTable } from "helpers/table-bag";
import { formatMessage } from "lib/globalize-util";
import { ajax } from "lib/observable";
import chunk from "lodash/chunk";
import { stringify } from "query-string";
import { updateEntities } from "redux-query";
import { Observable } from "rxjs";
import siteConfig from "site-config";

interface IRawInventoryUnits {
    itemId: string;
    skuIds: string[];
    sizeCodes: string[];
    quantities?: string[];
}

export const fetchProductEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.PRODUCT.FETCH.START)
        .map(({ payload }) => payload)
        .switchMap(({ id, brandId }) => {
            const { entities } = store.getState();

            const query: any = {
                __fields: "__all",
                __populate: "__variant",
                __view_disabled: true,
                __meta: true,
            };

            if (entities.collectionProducts && entities.collectionProducts[id]) {
                return [
                    {
                        meta: {
                            product: entities.collectionProducts[id],
                            brandId,
                        },
                        payload: id,
                        type: COLLECTIONS.COLLECTION.PRODUCT.FETCH.COMPLETED,
                    },
                ];
            } else {
                const endpoint = {
                    activeBrand: brandId,
                    url: `${siteConfig.api}/api/v3.0/item/${id}?${stringify(query)}`,
                };

                return ajax(endpoint)
                    .flatMap(({ response, status, xhr }) => {
                        const data = { [id]: response };
                        return [
                            updateEntities({
                                collectionProducts: (currentProducts) => {
                                    return {
                                        ...currentProducts,
                                        ...data,
                                    };
                                },
                            }),
                            {
                                meta: {
                                    product: response,
                                    brandId,
                                },
                                payload: id,
                                type: COLLECTIONS.COLLECTION.PRODUCT.FETCH.COMPLETED,
                            },
                        ];
                    })
                    .catch((error) =>
                        Observable.merge(
                            Observable.of({
                                error: true,
                                payload: error,
                                type: COLLECTIONS.COLLECTION.PRODUCT.FETCH.COMPLETED,
                            })
                        )
                    );
            }
        });

export const fetchProductVariantsEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.PRODUCT.FETCH.COMPLETED)
        .filter(({ error, meta }) => {
            return !error && meta.product;
        })
        .switchMap(({ meta: { product, brandId } }) => {
            const query: any = {
                __fields: "__all",
                __limit: 1000,
                __populate: "__variant",
                __meta: true,
                style_number: product.style_number,
            };

            const endpoint = {
                activeBrand: brandId,
                url: `${siteConfig.api}/api/v3.0/items?${stringify(query)}`,
            };

            return ajax(endpoint)
                .flatMap(({ response, status, xhr }) => {
                    response = response.data || response;
                    response.sort((a, b) => {
                        if (a.color < b.color) {
                            return -1;
                        }
                        if (a.color > b.color) {
                            return 1;
                        }
                        return 0;
                    });
                    const result = [];
                    const data = response.reduce((accu, prod) => {
                        result.push(prod._id);
                        accu[prod._id] = prod;
                        return accu;
                    }, {});

                    return [
                        updateEntities({
                            collectionProducts: (currentProducts) => {
                                return {
                                    ...currentProducts,
                                    ...data,
                                };
                            },
                        }),
                        {
                            payload: result,
                            type: COLLECTIONS.COLLECTION.PRODUCT.VARIANTS.FETCH.COMPLETED,
                        },
                    ];
                })
                .catch((error) =>
                    Observable.merge(
                        Observable.of({
                            error: true,
                            payload: error,
                            type: COLLECTIONS.COLLECTION.PRODUCT.VARIANTS.FETCH.COMPLETED,
                        })
                    )
                );
        });

export const syncInventoryUnitsEpic = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.START)
        .map((action) => action.payload)
        .switchMap((payload: ISyncInventoryUnitsPayload) => {
            const { itemIds, warehouseId, collectionId, merchantId } = payload;

            const state = store.getState();
            const { mutate } = state;
            const collectionObj = mutate.snapshots.get(collectionId);
            const itemsObjectId = collectionObj ? collectionObj.getIn(["items", "ref", "object_id"]) : undefined;
            const collectionItems = mutate.snapshots.get(itemsObjectId);

            const rowUnits = itemIds.reduce<IRawInventoryUnits[]>((acc, itemId) => {
                const rowIndex =
                    collectionItems &&
                    collectionItems.findIndex((collectionItem) => collectionItem.getIn(Item.FIELDS.ID) === itemId);

                const row = collectionItems && collectionItems.get(rowIndex);
                if (row) {
                    const skuIds = row.getIn(Item.FIELDS.SIZE_IDS)?.split(",");
                    const sizeCodes = row.getIn(Item.FIELDS.VENDOR_SIZES)?.split(",");
                    skuIds?.length &&
                        sizeCodes?.length &&
                        acc.push({
                            itemId,
                            skuIds,
                            sizeCodes,
                        });
                }

                return acc;
            }, []);

            return Observable.from(chunk(rowUnits, MAX_ITEMS_PER_REQUEST))
                .delay(1)
                .concatMap((items) => {
                    return ajax(
                        `${siteConfig.api}/api/v3.0/warehouse/${warehouseId}/skus/available-inventory?${stringify({
                            __retailer_merchant_id: merchantId,
                            sku_id: [
                                ...new window.Set(
                                    items.reduce((acc, item) => {
                                        acc.push(...item.skuIds);
                                        return acc;
                                    }, [])
                                ),
                            ],
                        })}`
                    )
                        .map(({ response }) => {
                            const rowsToUpdate = items.map<IRawInventoryUnits>((item) => ({
                                ...item,
                                quantities: item.skuIds.map((skuId) => {
                                    const inventory = response.find((inv) => inv.sku_id === skuId);
                                    return `${inventory?.available ?? "-"}`;
                                }),
                            }));
                            return rowsToUpdate;
                        })
                        .concatMap((rowsToUpdate) => {
                            rowsToUpdate.forEach((rowToUpdate) => {
                                const rowIndex =
                                    collectionItems &&
                                    collectionItems.findIndex(
                                        (collectionItem) => collectionItem.getIn(Item.FIELDS.ID) === rowToUpdate.itemId
                                    );
                                let updatedRow = collectionItems && collectionItems.get(rowIndex);

                                if (updatedRow) {
                                    const inventoryUnits = rowToUpdate.sizeCodes.map((code, codeIndex) => [
                                        code,
                                        rowToUpdate.quantities[codeIndex],
                                    ]);
                                    updatedRow = updatedRow.setIn(Item.INVENTORY_UNITS, flatToTable(inventoryUnits));

                                    store.dispatch(
                                        mutateCollectionItem({
                                            itemsId: ITEMS_ID,
                                            index: rowIndex,
                                            item: updatedRow,
                                        })
                                    );
                                }
                            });

                            return rowsToUpdate;
                        })
                        .catch((error) => {
                            return Observable.throw(error);
                        });
                })
                .reduce((acc, item) => {
                    acc.push(item);
                    return acc;
                }, [])
                .concatMap(() => {
                    return Observable.merge(
                        Observable.of({
                            type: COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.STOP,
                        }),
                        Observable.of({
                            type: COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.COMPLETED,
                        })
                    );
                })
                .catch(() => {
                    const errorMessage = formatMessage("Error syncing Inventory Units");
                    return Observable.merge(
                        Observable.of(snackbarAdd({ message: errorMessage })),
                        Observable.of({
                            type: COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.STOP,
                        })
                    );
                })
                .takeUntil(action$.ofType(COLLECTIONS.COLLECTION.PRODUCT.INVENTORY.SYNC_UNITS.STOP));
        });
};
